import React, { useState, useEffect } from 'react';
import { fetchArchivedJobs, unarchiveJob, fetchChoices } from '../api'; // Add unarchiveJob import
import './jobarchived.css';
import JobModal from './jobmodal';
import { logComponent, logDiagnostic } from '../../utils/diagnosticLogger';

const JobArchived = () => {
  const [jobs, setJobs] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedContact, setSelectedContact] = useState('');
  const [selectedService, setSelectedService] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [sortByDatePromised, setSortByDatePromised] = useState(false);
  const [reverseStatusOrder, setReverseStatusOrder] = useState(false);
  const [showBuffers, setShowBuffers] = useState(true);
  const [showColors, setShowColors] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [choices, setChoices] = useState({});
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [paginationInfo, setPaginationInfo] = useState({});
  
  // Log component mount
  useEffect(() => {
    logComponent('JobArchived', 'mount', {
      pathname: window.location.pathname,
      navigationType: performance.getEntriesByType('navigation')[0]?.type || 'unknown',
      timestamp: Date.now()
    }, true); // Send immediately
    
    return () => {
      logComponent('JobArchived', 'unmount', {
        jobsCount: jobs.length,
        timestamp: Date.now()
      }, true); // Send immediately
    };
  }, []);
  
  // Detect window resize for mobile view
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Function to format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return 'No Date';
    
    try {
      // On mobile, show short format: MM/DD
      if (isMobile) {
        const date = new Date(dateStr);
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${month}/${day}`;
      }
      
      // Desktop: show full date
      return dateStr;
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateStr;
    }
  };

  // Load choices from API
  useEffect(() => {
    const loadChoices = async () => {
      try {
        const choicesData = await fetchChoices();
        setChoices(choicesData);
      } catch (error) {
        console.error('Error loading choices:', error);
      }
    };
    loadChoices();
  }, []);

  // Status order comes from the editable status choices (Choice Manager).
  // Any status on a job that isn't in that list is appended so those jobs still render.
  const getStatusOrderList = () => {
    const configured = [...(choices.status || [])]
      .sort((a, b) => a.order - b.order)
      .map(choice => choice.value);
    const extra = [...new Set(
      jobs.map(job => job.status).filter(status => status && !configured.includes(status))
    )];
    return [...configured, ...extra];
  };

  // Get the current status order based on reverseStatusOrder
  const getCurrentStatusOrder = () => {
    const order = getStatusOrderList();
    return reverseStatusOrder ? order.reverse() : order;
  };

  const toggleContact = (contact) => {
    setSelectedContact(contact);
  };

  const toggleService = (service) => {
    setSelectedService(service);
  };

  const toggleStatus = (status) => {
    setSelectedStatus(status);
  };

  const toggleSortByDatePromised = () => {
    setSortByDatePromised(!sortByDatePromised);
  };

  const toggleModal = () => {
    setShowModal(!showModal);
  };

  const handleJobClick = (job) => {
    setSelectedJob(job);
    setShowModal(true);
  };

  const handleJobUpdate = (updatedJob) => {
    if (updatedJob.is_archived === false) {
      // If job was unarchived, remove it from the list and close modal
      setJobs(prevJobs => prevJobs.filter(job => job.id !== updatedJob.id));
      setShowModal(false);
    } else {
      // Otherwise just update the job in the list
      setJobs(prevJobs => prevJobs.map(job => job.id === updatedJob.id ? updatedJob : job));
    }
  };

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleNextPage = () => {
    if (paginationInfo.hasNext) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (paginationInfo.hasPrevious) {
      setCurrentPage(currentPage - 1);
    }
  };

  const getRowClassName = (job) => {
    if (!showColors) return '';
    if (!job.date_promised) return '';
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const promisedDate = new Date(job.date_promised);
    promisedDate.setHours(0, 0, 0, 0);
    
    if (promisedDate < today) {
      return 'past-due';
    }
    
    if (promisedDate.getTime() === today.getTime()) {
      return 'due-today';
    }
    
    return '';
  };

  useEffect(() => {
    const loadJobs = async () => {
      logDiagnostic('api', 'fetchArchivedJobs-start', {
        contact: selectedContact,
        service: selectedService,
        search: searchQuery,
        status: selectedStatus,
        page: currentPage,
        timestamp: Date.now()
      }, true); // Send immediately
      
      try {
        const response = await fetchArchivedJobs(selectedContact, selectedService, searchQuery, selectedStatus, currentPage, 50);
        
        logDiagnostic('api', 'fetchArchivedJobs-success', {
          jobsCount: response?.results?.length || 0,
          totalPages: response?.total_pages || 0,
          timestamp: Date.now()
        }, true); // Send immediately
        
        if (response && response.results) {
          setJobs(response.results);
          setTotalPages(response.total_pages || 1);
          setTotalCount(response.count || 0);
          setPaginationInfo({
            hasNext: response.has_next || false,
            hasPrevious: response.has_previous || false,
            nextPage: response.next_page || null,
            previousPage: response.previous_page || null
          });
        } else {
          // Handle case where response doesn't have expected structure
          logDiagnostic('api', 'fetchArchivedJobs-no-results', {
            response: response ? 'exists' : 'null',
            timestamp: Date.now()
          }, true);
          setJobs([]);
          setTotalPages(1);
          setTotalCount(0);
          setPaginationInfo({
            hasNext: false,
            hasPrevious: false,
            nextPage: null,
            previousPage: null
          });
        }
      } catch (error) {
        console.error('Error loading archived jobs:', error);
        logDiagnostic('api', 'fetchArchivedJobs-error', {
          error: error.message,
          status: error.response?.status,
          stack: error.stack,
          url: window.location.href,
          pathname: window.location.pathname,
          timestamp: Date.now()
        }, true); // Send immediately - critical error
        
        // Check if this is an auth error that might cause redirect
        if (error.response?.status === 401 || error.response?.status === 403) {
          logDiagnostic('api', 'fetchArchivedJobs-auth-error', {
            status: error.response.status,
            mightCauseReload: true,
            timestamp: Date.now()
          }, true); // Send immediately
        }
        
        setJobs([]);
        setTotalPages(1);
        setTotalCount(0);
        setPaginationInfo({
          hasNext: false,
          hasPrevious: false,
          nextPage: null,
          previousPage: null
        });
      }
    };

    loadJobs();
  }, [selectedContact, selectedService, searchQuery, selectedStatus, currentPage]);

  // Reset to page 1 when search query or filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedContact, selectedService, selectedStatus]);

  return (
    <div className='jobarchived'>
      <div className='jobarchivedfilters'>
        <div className='jobarchivedfiltersrow'>
          <div className='jobarchivedstatusfilter'>
            <label className='jobarchivedstatuslabel'>
              <span className='jobarchivedlabeltext'>Status:</span>
              <select className='jobarchivedstatusselect' value={selectedStatus} onChange={(e) => toggleStatus(e.target.value)}>
                <option value="all">All</option>
                {choices.status && [...choices.status]
                  .sort((a, b) => a.order - b.order)
                  .map(choice => (
                    <option key={choice.value} value={choice.value}>
                      {choice.display_name}
                    </option>
                  ))}
              </select>
            </label>
          </div>
          
          <div className='jobarchivedstatusfilter'>
            <label className='jobarchivedstatuslabel'>
              <span className='jobarchivedlabeltext'>Contact:</span>
              <select className='jobarchivedstatusselect' value={selectedContact} onChange={(e) => toggleContact(e.target.value)}>
                <option value="">All</option>
                <option value="Dave">Dave</option>
                <option value="Jared">Jared</option>
                <option value="Sam">Sam</option>
                <option value="Jake">Jake</option>
                <option value="Retail">Retail</option>
                <option value="Volvo">Volvo</option>
                <option value="Jesse">Jesse</option>
              </select>
            </label>
          </div>

          <div className='jobarchivedstatusfilter'>
            <label className='jobarchivedstatuslabel'>
              <span className='jobarchivedlabeltext'>Service:</span>
              <select className='jobarchivedstatusselect' value={selectedService} onChange={(e) => toggleService(e.target.value)}>
                <option value="">All</option>
                <option value="restoration">Restoration</option>
                <option value="straightening">Straightening</option>
                <option value="weld">Weld</option>
                <option value="powdercoating">Powder Coating</option>
                <option value="mount&balance">Mount & Balance</option>
              </select>
            </label>
          </div>

          <button className='jobarchivedsortButton' onClick={toggleSortByDatePromised}>
            {sortByDatePromised ? 'Due X' : 'Due'}
          </button>

          <button className='jobarchivedsortButton' onClick={() => setReverseStatusOrder(!reverseStatusOrder)}>
            {reverseStatusOrder ? 'Status ↑' : 'Status ↓'}
          </button>

          <button className='jobarchivedsortButton' onClick={() => setShowBuffers(!showBuffers)}>
            {showBuffers ? 'No Stations' : 'Stations'}
          </button>

          <button className='jobarchivedsortButton' onClick={() => setShowColors(!showColors)}>
            {showColors ? 'No Colors' : 'Colors'}
          </button>
        </div>
        <label className='jobarchivedstatuslabel jobarchivedsearchlabel'>
          <span className='jobarchivedlabeltext'>Search:</span>
          <input
            className='jobarchivedsearchbar'
            type="text"
            placeholder="⌕ Search Jobs"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </label>
      </div>

      <div className='jobarchivedlabelsparent'>
        <div className='jobarchivedlabels'>
          <h5 className='jobarchivedlabelsheaders'>Contact</h5>
          <h5 className='jobarchivedlabelsheaders'>Qty</h5>
          <h5 className='jobarchivedlabelsheaders'>Make</h5>
          <h5 className='jobarchivedlabelsheaders'>Service</h5>
          <h5 className='jobarchivedlabelsheaders'>Due</h5>
          <h5 className='jobarchivedlabelsheaders'>Customer</h5>
          <h5 className='jobarchivedlabelsheaders'>Job #</h5>
        </div>
        <hr className='jobarchivedlabelsheadersline'/>
      </div>

      <div className='jobarchivedscroll'>
        <div className='jobarchivedscrollinner'>
        {jobs && jobs.length > 0 ? (
          <>
            {showBuffers ? (
              <>
                {/* Show jobs with no status first */}
                {(() => {
                  const noStatusJobs = jobs.filter(job => !job.status);
                  if (noStatusJobs.length > 0) {
                    return (
                      <React.Fragment>
                        <div className='statusbuffer'>
                          <div className='statusbuffertext'>No Status</div>
                        </div>
                        {noStatusJobs
                          .sort((a, b) => {
                            if (sortByDatePromised) {
                              return new Date(a.date_promised) - new Date(b.date_promised);
                            }
                            return new Date(b.date_created) - new Date(a.date_created);
                          })
                          .map(job => (
                            <div 
                              className={`jobarchivedrow ${getRowClassName(job)}`} 
                              key={job.id} 
                              onClick={() => handleJobClick(job)}
                            >
                              <div className='jobarchivedrowdataname'>{job.contact || 'No Contact'}</div>
                              <div className='jobarchivedrowdataname'>{job.quantity || 'N/A'}</div>
                              <div className='jobarchivedrowdatavehicle'>{job.make || 'No Make'}</div>
                              <div className='jobarchivedrowdataservice'>{job.service || 'No Service'}</div>
                              <div className='jobarchivedrowdatadate'>{formatDate(job.date_promised)}</div>
                              <div className='jobarchivedrowdatacustomer'>{job.customer_name || job.customer || 'No Customer'}</div>
                              <div className='jobarchivedrowdatacustomer'>{job.id}</div>
                            </div>
                          ))}
                      </React.Fragment>
                    );
                  }
                  return null;
                })()}

                {/* Show jobs with statuses */}
                {getCurrentStatusOrder().map(status => {
                  const statusJobs = jobs.filter(job => job.status === status);
                  if (statusJobs.length === 0) return null;

                  return (
                    <React.Fragment key={status}>
                      <div className='statusbuffer'>
                        <div className='statusbuffertext'>{status}</div>
                      </div>
                      {statusJobs
                        .sort((a, b) => {
                          if (sortByDatePromised) {
                            return new Date(a.date_promised) - new Date(b.date_promised);
                          }
                          return new Date(b.date_created) - new Date(a.date_created);
                        })
                        .map(job => (
                          <div 
                            className={`jobarchivedrow ${getRowClassName(job)}`} 
                            key={job.id} 
                            onClick={() => handleJobClick(job)}
                          >
                            <div className='jobarchivedrowdataname'>{job.contact || 'No Contact'}</div>
                            <div className='jobarchivedrowdataname'>{job.quantity || 'N/A'}</div>
                            <div className='jobarchivedrowdatavehicle'>{job.make || 'No Make'}</div>
                            <div className='jobarchivedrowdataservice'>{job.service || 'No Service'}</div>
                            <div className='jobarchivedrowdatadate'>{formatDate(job.date_promised)}</div>
                            <div className='jobarchivedrowdatacustomer'>{job.customer_name || job.customer || 'No Customer'}</div>
                            <div className='jobarchivedrowdatacustomer'>{job.id}</div>
                          </div>
                        ))}
                    </React.Fragment>
                  );
                })}
              </>
            ) : (
              // Flat list view
              jobs
                .sort((a, b) => {
                  if (sortByDatePromised) {
                    return new Date(a.date_promised) - new Date(b.date_promised);
                  }
                  return new Date(b.date_created) - new Date(a.date_created);
                })
                .map(job => (
                  <div 
                    className={`jobarchivedrow ${getRowClassName(job)}`} 
                    key={job.id} 
                    onClick={() => handleJobClick(job)}
                  >
                    <div className='jobarchivedrowdataname'>{job.contact || 'No Contact'}</div>
                    <div className='jobarchivedrowdataname'>{job.quantity || 'N/A'}</div>
                    <div className='jobarchivedrowdatavehicle'>{job.make || 'No Make'}</div>
                    <div className='jobarchivedrowdataservice'>{job.service || 'No Service'}</div>
                    <div className='jobarchivedrowdatadate'>{formatDate(job.date_promised)}</div>
                    <div className='jobarchivedrowdatacustomer'>{job.customer_name || job.customer || 'No Customer'}</div>
                    <div className='jobarchivedrowdatacustomer'>{job.id}</div>
                  </div>
                ))
            )}
          </>
        ) : (
          <p>No archived jobs found</p>
        )}
        
        {/* Pagination */}
        {totalPages > 1 && (
        <div className="jobarchived-pagination">
          <div className="jobarchived-pagination-info">
            Showing {((currentPage - 1) * 50) + 1} to {Math.min(currentPage * 50, totalCount)} of {totalCount} archived jobs
          </div>
          <div className="jobarchived-pagination-controls">
            <button 
              className="jobarchived-pagination-btn"
              onClick={handlePreviousPage}
              disabled={!paginationInfo.hasPrevious}
            >
              ← Previous
            </button>
            
            <div className="jobarchived-pagination-pages">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <button
                    key={pageNum}
                    className={`jobarchived-pagination-page ${
                      pageNum === currentPage ? 'active' : ''
                    }`}
                    onClick={() => handlePageChange(pageNum)}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            
            <button 
              className="jobarchived-pagination-btn"
              onClick={handleNextPage}
              disabled={!paginationInfo.hasNext}
            >
              Next →
            </button>
          </div>
        </div>
        )}
        </div>
      </div>

      <JobModal 
        showModal={showModal} 
        toggleModal={() => setShowModal(false)} 
        job={selectedJob}
        onJobUpdated={handleJobUpdate}
      />
    </div>
  );
};

export default JobArchived;
